import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth, firestore } from '../firebase';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import SymptomTracker from './SymptomTracker';
import HealthInsights from './HealthInsights';
import AIHealthAssistant from './AIHealthAssistant';
import HealthDataVisualization from './HealthDataVisualization';
import MedicationManager from './MedicationManager';
import './Dashboard.css';

const Dashboard = () => {
  const [healthData, setHealthData] = useState({ symptoms: [], insights: [] });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSymptoms: 0,
    activeSymptoms: 0,
    medications: 0,
    insights: 0
  });

  useEffect(() => {
    fetchUserHealthData();
  }, []);

  const fetchUserHealthData = async () => {
    setLoading(true);
    try {
      const [symptomsSnapshot, insightsSnapshot, medicationsSnapshot] = await Promise.all([
        getDocs(query(collection(firestore, 'symptoms'), where('userId', '==', auth.currentUser.uid))),
        getDocs(query(collection(firestore, 'insights'), where('userId', '==', auth.currentUser.uid))),
        getDocs(query(collection(firestore, 'medications'), where('userId', '==', auth.currentUser.uid)))
      ]);

      const symptoms = symptomsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const insights = insightsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      setHealthData({ symptoms, insights });
      setStats({
        totalSymptoms: symptoms.length,
        activeSymptoms: symptoms.filter(s => s.active).length,
        medications: medicationsSnapshot.size,
        insights: insights.length
      });
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSymptom = async (symptom) => {
    try {
      await addDoc(collection(firestore, 'symptoms'), {
        ...symptom,
        userId: auth.currentUser.uid,
        timestamp: new Date(),
        active: true
      });
      fetchUserHealthData();
    } catch (error) {
      console.error('Error adding symptom: ', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>HealthFlow Dashboard</h1>
          <button onClick={handleLogout} className="logout-btn">
            <i className="fas fa-sign-out-alt"></i> Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="quick-stats">
          <div className="stat-card">
            <h3>Total Symptoms</h3>
            <div className="stat-value">{stats.totalSymptoms}</div>
          </div>
          <div className="stat-card">
            <h3>Active Symptoms</h3>
            <div className="stat-value">{stats.activeSymptoms}</div>
          </div>
          <div className="stat-card">
            <h3>Medications</h3>
            <div className="stat-value">{stats.medications}</div>
          </div>
          <div className="stat-card">
            <h3>Health Insights</h3>
            <div className="stat-value">{stats.insights}</div>
          </div>
        </div>

        <div className="dashboard-grid">
          <div className="dashboard-column main-column">
            <section className="dashboard-section">
              <div className="section-header">
                <h2>Symptom Trends</h2>
              </div>
              <div className="section-content">
                <HealthDataVisualization healthData={healthData} />
              </div>
            </section>
            
            <section className="dashboard-section">
              <div className="section-header">
                <h2>Track Symptoms</h2>
              </div>
              <div className="section-content">
                <SymptomTracker 
                  onAddSymptom={handleAddSymptom} 
                  symptoms={healthData.symptoms} 
                />
              </div>
            </section>
          </div>

          <div className="dashboard-column sidebar-column">
            <section className="dashboard-section">
              <div className="section-header">
                <h2>Health Insights</h2>
              </div>
              <div className="section-content">
                <HealthInsights insights={healthData.insights} />
              </div>
            </section>

            <section className="dashboard-section">
              <div className="section-header">
                <h2>AI Health Assistant</h2>
              </div>
              <div className="section-content">
                <AIHealthAssistant healthData={healthData} />
              </div>
            </section>

            <section className="dashboard-section">
              <div className="section-header">
                <h2>Medication Manager</h2>
              </div>
              <div className="section-content">
                <MedicationManager />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
